export * as form from './form/dlpOrderForm.selectors';
export * as order from './order/order.selector';

import { createSelector } from 'reselect';
import { DlpOrderFormModel } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import { getMaxAsk, getMaxBid } from 'state/secondaryMarket/referential/dlpProduct/dlpProduct.selectors';
import {
  MANDATORY_FIELDS_WARNING,
  MISSING_CUSTODIAN_ERROR,
  MISSING_LIMIT_DATE_ERROR,
  MISSING_LIMIT_ERROR,
  MISSING_LIMIT_TIME_ERROR,
  MISSING_MANDATORY_QUANTITY_ERROR,
  mandatoryFieldsErrors,
  QUANTITY_NOT_IN_RANGE,
  SecondaryMarketFormError,
} from 'state/secondaryMarket/shared/form/validationResults';

import {
  GetValidationErrorsFunc,
  getInputFormCustodian,
  getInputFormLimit,
  getInputFormLimitDate,
  getInputFormLimitTime,
  getInputFormOrderType,
  getInputFormQuantity,
  getInputFormTradeWay,
  getInputFormValidity,
} from './form/dlpOrderForm.selectors';

const MAX_QUANTITY = 2_000_000_000;
export const getMaxQuantity = createSelector(
  [getInputFormTradeWay, getMaxBid, getMaxAsk],
  (tradeWay, maxBid, maxAsk) => (tradeWay === 'ClientBuy' ? maxAsk : maxBid) ?? MAX_QUANTITY,
);

export const makeGetDlpOrderValidationErrors: () => GetValidationErrorsFunc = () =>
  createSelector(
    [
      getInputFormQuantity,
      getInputFormLimit,
      getInputFormOrderType,
      getInputFormCustodian,
      getInputFormValidity,
      getInputFormLimitDate,
      getInputFormLimitTime,
      getMaxQuantity,
    ],
    (quantity, limit, orderType, custodian, validity, limitDate, limitTime, maxQuantity) => {
      const errors: SecondaryMarketFormError<DlpOrderFormModel>[] = [];

      if (quantity === undefined) {
        errors.push(MISSING_MANDATORY_QUANTITY_ERROR as SecondaryMarketFormError<DlpOrderFormModel>);
      } else if (quantity < 1 || quantity > maxQuantity) {
        errors.push(
          QUANTITY_NOT_IN_RANGE({
            min: 1,
            max: maxQuantity,
          }) as SecondaryMarketFormError<DlpOrderFormModel>,
        );
      }

      if (custodian === undefined) {
        errors.push(MISSING_CUSTODIAN_ERROR as SecondaryMarketFormError<DlpOrderFormModel>);
      }

      if (orderType === 'Limit' && limit === undefined) {
        errors.push(MISSING_LIMIT_ERROR as SecondaryMarketFormError<DlpOrderFormModel>);
      }

      if (validity === 'GTD' && limitDate === undefined) {
        errors.push(MISSING_LIMIT_DATE_ERROR as SecondaryMarketFormError<DlpOrderFormModel>);
      }
      if (validity === 'GTD' && limitTime === undefined) {
        errors.push(MISSING_LIMIT_TIME_ERROR as SecondaryMarketFormError<DlpOrderFormModel>);
      }

      if (errors.map(e => e.translationKey).some(e => e && mandatoryFieldsErrors.includes(e))) {
        errors.push(MANDATORY_FIELDS_WARNING as SecondaryMarketFormError<DlpOrderFormModel>);
      }

      return errors;
    },
  );
